import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Eye,
  Hash,
  ImagePlus,
  Info,
  Loader2,
  Palette,
  RefreshCw,
  Save,
  School,
  Upload,
  X,
} from 'lucide-react'
import { schoolsApi } from '../api/services.js'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import PageHeader from '../components/PageHeader.jsx'
import { setSchoolConfig } from '../store/authSlice.js'
import { buildSchoolConfigFromSchool } from '../utils/schoolConfig.js'

const PRESET_COLORS = [
  '#2563eb',
  '#7c3aed',
  '#db2777',
  '#dc2626',
  '#ea580c',
  '#d97706',
  '#16a34a',
  '#0891b2',
]

const FORMAT_TOKENS = [
  { token: '{SCHOOL}', desc: 'School code', example: 'DPS' },
  { token: '{YEAR}', desc: 'Full year', example: '2024' },
  { token: '{YY}', desc: '2-digit year', example: '24' },
  { token: '{SEQ}', desc: '4-digit number', example: '0001' },
  { token: '{SEQ3}', desc: '3-digit number', example: '001' },
  { token: '{SEQ5}', desc: '5-digit number', example: '00001' },
]

const FORMAT_PRESETS = [
  { label: 'DPS-2024-0001', format: '{SCHOOL}-{YEAR}-{SEQ}' },
  { label: 'DPS/24/001', format: '{SCHOOL}/{YY}/{SEQ3}' },
  { label: 'ADM-2024-0001', format: 'ADM-{YEAR}-{SEQ}' },
  { label: '2024-0001', format: '{YEAR}-{SEQ}' },
  { label: '00001', format: '{SEQ5}' },
]

const REPORT_CARD_TEMPLATES = [
  { key: 'modern', label: 'Modern', desc: 'Cards, gradient header, visual layout' },
  { key: 'classic', label: 'Classic', desc: 'Traditional bordered table, formal style' },
  { key: 'minimal', label: 'Minimal', desc: 'Clean, B&W-friendly, print-optimized' },
]

const defaultSchool = {
  name: '',
  schoolCode: '',
  email: '',
  phone: '',
  address: '',
  website: '',
}

const defaultBranding = {
  appName: '',
  primaryColor: '#2563eb',
  secondaryColor: '#16a34a',
  darkMode: false,
  logoUrl: '',
  fontFamily: 'Inter',
  bannerUrl: '',
  splashUrl: '',
  appIcon: '',
  reportCardTemplate: 'modern',
}

const defaultRazorpay = {
  keyId: '',
  keySecret: '',
  isEnabled: false,
  hasKeySecret: false,
}

const resolveBrandName = (appName, schoolName) => {
  if (appName?.trim()) return appName.trim()
  if (schoolName?.trim()) return schoolName.trim()
  return 'BrainCamp'
}

const previewFormat = (format, schoolCode = 'DPS') => {
  const year = new Date().getFullYear().toString()
  const yy = year.slice(-2)
  const pad = (value, len) => String(value).padStart(len, '0')

  return format
    .replaceAll('{SCHOOL}', schoolCode.toUpperCase())
    .replaceAll('{YEAR}', year)
    .replaceAll('{YY}', yy)
    .replaceAll('{SEQ5}', pad(1, 5))
    .replaceAll('{SEQ4}', pad(1, 4))
    .replaceAll('{SEQ3}', pad(1, 3))
    .replaceAll('{SEQ}', pad(1, 4))
}

const buildPreviewHtml = (template, { appName, schoolName, logoUrl, primaryColor, secondaryColor }) => {
  const brandName = resolveBrandName(appName, schoolName)
  const primary = primaryColor || '#2563eb'
  const secondary = secondaryColor || '#16a34a'
  const logo = logoUrl || ''
  const initials = brandName.slice(0, 1).toUpperCase() || 'S'
  const logoTag = logo
    ? `<img class="logo" src="${logo}" alt="logo" />`
    : `<div class="logo-fallback">${initials}</div>`

  const subjectRows = [
    { subject: 'Mathematics', obtained: 88, total: 100, percentage: 88, grade: 'A+' },
    { subject: 'English', obtained: 76, total: 100, percentage: 76, grade: 'A' },
    { subject: 'Science', obtained: 91, total: 100, percentage: 91, grade: 'A+' },
    { subject: 'Social Studies', obtained: 65, total: 100, percentage: 65, grade: 'B' },
    { subject: 'Hindi', obtained: 82, total: 100, percentage: 82, grade: 'A' },
  ]

  const gradeColor = (percentage) => {
    if (percentage >= 75) return '#15803d'
    if (percentage >= 50) return '#d97706'
    return '#dc2626'
  }

  if (template === 'classic') {
    const rows = subjectRows
      .map((row, index) => {
        const color = gradeColor(row.percentage)
        return `<tr style="background:${index % 2 === 0 ? '#fff' : '#f8fafc'}">
          <td style="padding:9px 12px;border:1px solid #d0d7e2;font-weight:600">${row.subject}</td>
          <td style="padding:9px 12px;border:1px solid #d0d7e2;text-align:center">${row.obtained}</td>
          <td style="padding:9px 12px;border:1px solid #d0d7e2;text-align:center">${row.total}</td>
          <td style="padding:9px 12px;border:1px solid #d0d7e2;text-align:center;color:${color};font-weight:700">${row.percentage}%</td>
          <td style="padding:9px 12px;border:1px solid #d0d7e2;text-align:center;color:${color};font-weight:700">${row.grade}</td>
        </tr>`
      })
      .join('')

    return `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <style>
            * { box-sizing: border-box; }
            body { margin: 0; padding: 16px; font-family: Arial, sans-serif; background: #f3f7fb; color: #0f172a; }
            .page { background: #fff; border: 3px solid ${primary}; border-radius: 4px; overflow: hidden; }
            .strip { background: ${primary}; height: 8px; }
            .header { padding: 16px 20px; border-bottom: 2px solid ${primary}; display: flex; align-items: center; gap: 14px; }
            .logo, .logo-fallback { width: 56px; height: 56px; border-radius: 8px; }
            .logo { object-fit: cover; border: 2px solid ${primary}44; }
            .logo-fallback { background: ${primary}22; border: 2px solid ${primary}44; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 700; color: ${primary}; }
            .school-name { margin: 0 0 3px; font-size: 18px; font-weight: 700; color: ${primary}; }
            .school-meta { font-size: 11px; color: #475569; }
            .report-title { margin: 0 20px; padding: 7px 20px; border-top: 1px solid ${primary}44; border-bottom: 1px solid ${primary}44; text-align: center; font-size: 14px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: ${primary}; }
            .student-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 5px 20px; padding: 14px 20px; }
            .student-row { display: flex; gap: 6px; font-size: 12px; }
            .student-label { color: #64748b; min-width: 100px; }
            .student-value { font-weight: 600; }
            .section-label { margin: 0 20px 6px; font-size: 12px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: ${primary}; }
            table { width: calc(100% - 40px); margin: 0 20px 16px; border-collapse: collapse; }
            thead th { padding: 9px 12px; background: ${primary}; border: 1px solid ${primary}; color: #fff; text-align: center; font-size: 12px; }
            thead th:first-child { text-align: left; }
            .summary { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin: 0 20px 16px; }
            .summary-card { border: 1px solid #d0d7e2; padding: 8px 12px; font-size: 12px; display: flex; gap: 8px; }
            .summary-label { color: #64748b; }
            .summary-value { font-weight: 700; font-size: 14px; }
            .signatures { display: flex; justify-content: space-between; margin: 16px 20px 12px; }
            .signature-box { width: 180px; border-top: 1px solid #999; padding-top: 6px; font-size: 12px; }
            .signature-box b { display: block; }
            .signature-box span { font-size: 10px; color: #64748b; }
            .footer { padding: 8px 20px 14px; font-size: 10px; text-align: center; color: #94a3b8; }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="strip"></div>
            <div class="header">
              ${logoTag}
              <div>
                <div class="school-name">${brandName}</div>
                <div class="school-meta">123 School Road, City · Ph: 9876543210 · info@school.edu</div>
              </div>
            </div>
            <div class="report-title">Progress Report Card — Term 1 (2024–25)</div>
            <div class="student-grid">
              <div class="student-row"><span class="student-label">Student Name :</span><span class="student-value">Rahul Sharma</span></div>
              <div class="student-row"><span class="student-label">Admission No :</span><span class="student-value">DPS-2024-0042</span></div>
              <div class="student-row"><span class="student-label">Class :</span><span class="student-value">10 A</span></div>
              <div class="student-row"><span class="student-label">Academic Year :</span><span class="student-value">2024–2025</span></div>
            </div>
            <div class="section-label">Scholastic Performance</div>
            <table>
              <thead>
                <tr>
                  <th style="text-align:left">Subject</th>
                  <th>Obtained</th>
                  <th>Total</th>
                  <th>Percentage</th>
                  <th>Grade</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
            <div class="section-label">Overall Result</div>
            <div class="summary">
              <div class="summary-card"><span class="summary-label">Total Marks</span><span class="summary-value">402 / 500</span></div>
              <div class="summary-card"><span class="summary-label">Percentage</span><span class="summary-value">80.4%</span></div>
              <div class="summary-card"><span class="summary-label">Overall Grade</span><span class="summary-value">A</span></div>
              <div class="summary-card"><span class="summary-label">Result</span><span class="summary-value" style="color:#15803d">Pass</span></div>
            </div>
            <div class="signatures">
              <div class="signature-box"><b>Class Teacher</b><span>Signature & Remarks</span></div>
              <div class="signature-box"><b>Principal</b><span>Authorized Signature</span></div>
            </div>
            <div class="footer">${brandName} · Report Card Preview</div>
          </div>
        </body>
      </html>`
  }

  if (template === 'minimal') {
    const rows = subjectRows
      .map((row, index) => `<tr style="background:${index % 2 === 0 ? '#fff' : '#f9fafb'}">
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb">${row.subject}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:center">${row.obtained}/${row.total}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:center">${row.percentage}%</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:700">${row.grade}</td>
      </tr>`)
      .join('')

    return `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <style>
            * { box-sizing: border-box; }
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; font-size: 13px; color: #111827; background: #fff; }
            .header { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; padding-bottom: 12px; border-bottom: 2px solid #111827; }
            .logo, .logo-fallback { width: 48px; height: 48px; border-radius: 6px; }
            .logo { object-fit: cover; }
            .logo-fallback { background: #f3f4f6; border: 1px solid #d1d5db; display: flex; align-items: center; justify-content: center; font-size: 20px; font-weight: 700; color: #374151; }
            .school-name { font-size: 17px; font-weight: 700; }
            .school-meta { margin-top: 2px; font-size: 11px; color: #6b7280; }
            .report-title { margin-bottom: 12px; padding-bottom: 7px; border-bottom: 1px solid #e5e7eb; font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #6b7280; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; margin-bottom: 16px; }
            .info-row { display: flex; gap: 6px; font-size: 12px; }
            .info-label { color: #6b7280; min-width: 95px; }
            .info-value { font-weight: 600; }
            .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
            .stat { border: 1px solid #e5e7eb; border-radius: 4px; padding: 9px 12px; }
            .stat-label { margin-bottom: 3px; font-size: 10px; letter-spacing: 0.8px; text-transform: uppercase; color: #9ca3af; }
            .stat-value { font-size: 17px; font-weight: 700; }
            .section-label { margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #e5e7eb; font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; }
            th { padding: 7px 10px; border-bottom: 2px solid #111827; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; text-align: left; text-transform: uppercase; color: #6b7280; }
            th:not(:first-child) { text-align: center; }
            .signatures { display: flex; justify-content: space-between; margin-top: 24px; padding-top: 10px; border-top: 1px solid #e5e7eb; }
            .signature-box { width: 160px; }
            .signature-label { margin-top: 36px; padding-top: 5px; border-top: 1px solid #9ca3af; font-size: 12px; font-weight: 700; }
            .signature-note { font-size: 10px; color: #9ca3af; }
            .footer { margin-top: 16px; font-size: 10px; text-align: center; color: #9ca3af; }
          </style>
        </head>
        <body>
          <div class="header">
            ${logoTag}
            <div>
              <div class="school-name">${brandName}</div>
              <div class="school-meta">123 School Road · Ph: 9876543210</div>
            </div>
          </div>
          <div class="report-title">Progress Report Card — Term 1 (2024–25)</div>
          <div class="info-grid">
            <div class="info-row"><span class="info-label">Student</span><span class="info-value">Rahul Sharma</span></div>
            <div class="info-row"><span class="info-label">Admission No</span><span class="info-value">DPS-2024-0042</span></div>
            <div class="info-row"><span class="info-label">Class</span><span class="info-value">10 A</span></div>
            <div class="info-row"><span class="info-label">Academic Year</span><span class="info-value">2024–2025</span></div>
          </div>
          <div class="stats">
            <div class="stat"><div class="stat-label">Score</div><div class="stat-value">80.4%</div></div>
            <div class="stat"><div class="stat-label">Grade</div><div class="stat-value">A</div></div>
            <div class="stat"><div class="stat-label">Passed</div><div class="stat-value">5/5</div></div>
            <div class="stat"><div class="stat-label">Marks</div><div class="stat-value">402/500</div></div>
          </div>
          <div class="section-label">Subject Performance</div>
          <table>
            <thead>
              <tr>
                <th>Subject</th>
                <th>Marks</th>
                <th>Percentage</th>
                <th>Grade</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="signatures">
            <div class="signature-box"><div class="signature-label">Class Teacher<div class="signature-note">Signature</div></div></div>
            <div class="signature-box"><div class="signature-label">Principal<div class="signature-note">Authorized Signature</div></div></div>
          </div>
          <div class="footer">${brandName} · Report Card Preview</div>
        </body>
      </html>`
  }

  const rows = subjectRows
    .map((row) => {
      const color = gradeColor(row.percentage)
      return `<div style="padding:12px 0;border-bottom:1px solid #e5e7eb">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
          <span style="font-weight:600;font-size:13px;color:#0f172a">${row.subject}</span>
          <div style="display:flex;align-items:center;gap:10px">
            <span style="font-size:12px;color:#64748b">${row.obtained}/${row.total}</span>
            <span style="background:${color}18;border:1px solid ${color}44;border-radius:8px;padding:2px 9px;font-size:12px;font-weight:700;color:${color}">${row.percentage}%</span>
          </div>
        </div>
        <div style="height:6px;border-radius:99px;background:#e5e7eb;overflow:hidden">
          <div style="height:6px;border-radius:99px;background:${color};width:${Math.min(row.percentage, 100)}%"></div>
        </div>
      </div>`
    })
    .join('')

  return `<!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <style>
          * { box-sizing: border-box; }
          body { margin: 0; padding: 16px; font-family: Arial, sans-serif; background: #f3f7fb; color: #0f172a; }
          .sheet { background: #fff; border: 1px solid #dbe4f0; border-radius: 20px; overflow: hidden; }
          .hero { padding: 22px 22px 18px; background: linear-gradient(135deg, ${primary}, ${secondary}); color: #fff; }
          .hero-top { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
          .brand { display: flex; align-items: center; gap: 12px; }
          .logo, .logo-fallback { width: 52px; height: 52px; border-radius: 14px; }
          .logo { object-fit: cover; border: 1px solid rgba(255, 255, 255, 0.25); }
          .logo-fallback { background: rgba(255, 255, 255, 0.18); border: 1px solid rgba(255, 255, 255, 0.25); display: flex; align-items: center; justify-content: center; font-size: 20px; font-weight: 700; color: #fff; }
          .school-name { margin: 0 0 2px; font-size: 17px; font-weight: 700; }
          .school-meta { font-size: 11px; opacity: 0.82; }
          .hero-bottom { margin-top: 18px; display: flex; justify-content: space-between; align-items: flex-end; gap: 14px; }
          .session-label { margin-bottom: 4px; font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; opacity: 0.78; }
          .session-title { font-size: 22px; font-weight: 700; }
          .session-sub { margin-top: 4px; font-size: 11px; opacity: 0.82; }
          .hero-pills { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; min-width: 220px; }
          .pill { padding: 10px 12px; border-radius: 14px; background: rgba(255, 255, 255, 0.12); border: 1px solid rgba(255, 255, 255, 0.22); }
          .pill-label { margin-bottom: 3px; font-size: 10px; letter-spacing: 1px; text-transform: uppercase; opacity: 0.78; }
          .pill-value { font-size: 18px; font-weight: 700; }
          .body { padding: 18px 20px; }
          .student-card { margin-bottom: 14px; padding: 16px; border: 1px solid #e5e7eb; border-radius: 16px; }
          .student-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
          .student-name { margin: 0 0 2px; font-size: 18px; font-weight: 700; }
          .student-meta { font-size: 12px; color: #64748b; }
          .student-initial { width: 44px; height: 44px; border-radius: 22px; background: ${primary}22; border: 2px solid ${primary}44; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 700; color: ${primary}; }
          .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 14px; }
          .stat { border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px; text-align: center; }
          .stat-value { font-size: 16px; font-weight: 700; }
          .stat-label { margin-top: 2px; font-size: 10px; color: #94a3b8; }
          .section-title { margin-bottom: 10px; font-size: 14px; font-weight: 700; color: #0f172a; }
        </style>
      </head>
      <body>
        <div class="sheet">
          <div class="hero">
            <div class="hero-top">
              <div class="brand">
                ${logoTag}
                <div>
                  <div class="school-name">${brandName}</div>
                  <div class="school-meta">123 School Road, City</div>
                </div>
              </div>
            </div>
            <div class="hero-bottom">
              <div>
                <div class="session-label">Session</div>
                <div class="session-title">Term 1 (2024–25)</div>
                <div class="session-sub">Apr 2024 – Sep 2024</div>
              </div>
              <div class="hero-pills">
                <div class="pill"><div class="pill-label">Score</div><div class="pill-value">80.4%</div></div>
                <div class="pill"><div class="pill-label">Grade</div><div class="pill-value">A</div></div>
              </div>
            </div>
          </div>
          <div class="body">
            <div class="student-card">
              <div class="student-row">
                <div>
                  <div class="student-name">Rahul Sharma</div>
                  <div class="student-meta">Class 10 A · Adm: DPS-2024-0042</div>
                </div>
                <div class="student-initial">R</div>
              </div>
            </div>
            <div class="stats">
              <div class="stat"><div class="stat-value" style="color:#15803d">5</div><div class="stat-label">Passed</div></div>
              <div class="stat"><div class="stat-value" style="color:#dc2626">0</div><div class="stat-label">Failed</div></div>
              <div class="stat"><div class="stat-value" style="color:#d97706">0</div><div class="stat-label">Absent</div></div>
              <div class="stat"><div class="stat-value">402/500</div><div class="stat-label">Marks</div></div>
            </div>
            <div class="section-title">Subject Performance</div>
            ${rows}
          </div>
        </div>
      </body>
    </html>`
}

export default function SettingsPage() {
  const navigate = useNavigate()
  const logoInputRef = useRef(null)
  const dispatch = useDispatch()
  const { user, schoolConfig } = useSelector((state) => state.auth)
  const schoolId = user?.schoolId?._id || user?.schoolId || user?.school?._id || user?.school || ''

  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [savingSection, setSavingSection] = useState('')
  const [school, setSchool] = useState(defaultSchool)
  const [branding, setBranding] = useState(defaultBranding)
  const [admissionFormat, setAdmissionFormat] = useState('{SCHOOL}-{YEAR}-{SEQ}')
  const [razorpay, setRazorpay] = useState(defaultRazorpay)
  const [isChangingRazorpaySecret, setIsChangingRazorpaySecret] = useState(false)
  const [pendingApproval, setPendingApproval] = useState(null)
  const [previewTemplate, setPreviewTemplate] = useState(null)

  const schoolLabel = school.name || user?.schoolName || 'School profile'
  const isSaving = (section) => savingSection === section
  const admissionPreview = useMemo(
    () => previewFormat(admissionFormat, school.schoolCode || 'SCH'),
    [admissionFormat, school.schoolCode]
  )
  const previewHtml = useMemo(() => {
    if (!previewTemplate) return ''
    return buildPreviewHtml(previewTemplate, {
      appName: branding.appName,
      schoolName: school.name,
      logoUrl: branding.logoUrl,
      primaryColor: branding.primaryColor,
      secondaryColor: branding.secondaryColor,
    })
  }, [previewTemplate, branding, school.name])

  const syncSessionSchoolConfig = (nextSchoolConfig) => {
    const normalized = buildSchoolConfigFromSchool(nextSchoolConfig, schoolConfig)
    if (normalized) {
      dispatch(setSchoolConfig(normalized))
    }
  }

  const hydrateSchoolSettings = (data) => {
    setSchool({
      name: data?.name || '',
      schoolCode: data?.schoolCode || '',
      email: data?.email || '',
      phone: data?.phone || '',
      address: data?.address || '',
      website: data?.website || '',
    })

    setBranding({
      appName: data?.theme?.appName || data?.name || '',
      primaryColor: data?.theme?.primaryColor || '#2563eb',
      secondaryColor: data?.theme?.secondaryColor || '#16a34a',
      darkMode: Boolean(data?.theme?.darkMode),
      logoUrl: data?.theme?.logoUrl || '',
      fontFamily: data?.theme?.fontFamily || 'Inter',
      bannerUrl: data?.theme?.bannerUrl || '',
      splashUrl: data?.theme?.splashUrl || '',
      appIcon: data?.theme?.appIcon || '',
      reportCardTemplate: data?.theme?.reportCardTemplate || 'modern',
    })

    setAdmissionFormat(data?.admissionFormat || '{SCHOOL}-{YEAR}-{SEQ}')
    setRazorpay({
      keyId: data?.razorpay?.keyId || '',
      keySecret: '',
      isEnabled: Boolean(data?.razorpay?.isEnabled),
      hasKeySecret: Boolean(data?.razorpay?.hasKeySecret),
    })
    setIsChangingRazorpaySecret(false)
    setPendingApproval(null)
    syncSessionSchoolConfig(data)
  }

  const fetchSettings = async ({ silent = false } = {}) => {
    if (!schoolId) {
      setLoading(false)
      return
    }

    try {
      if (silent) setRefreshing(true)
      else setLoading(true)

      const data = await schoolsApi.getById(schoolId)
      hydrateSchoolSettings(data)
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load school settings')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [schoolId])

  const runSave = async (section, action, successMessage) => {
    try {
      setSavingSection(section)
      await action()
      toast.success(successMessage)
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save settings')
    } finally {
      setSavingSection('')
    }
  }

  const handleUploadLogo = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || !schoolId) return

    const formData = new FormData()
    formData.append('logo', file)

    try {
      setSavingSection('logo')
      const response = await schoolsApi.uploadLogo(schoolId, formData)
      const logoUrl = response?.logoUrl || response?.fileUrl || ''
      if (logoUrl) {
        setBranding((prev) => ({ ...prev, logoUrl }))
        syncSessionSchoolConfig({
          logoUrl,
          theme: { logoUrl },
        })
      }
      toast.success('School logo updated')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload logo')
    } finally {
      setSavingSection('')
    }
  }

  const handleSaveBranding = async () => {
    if (!schoolId) return

    const resolvedAppName = resolveBrandName(branding.appName, school.name || user?.schoolName || '')
    const theme = {
      ...branding,
      appName: resolvedAppName,
    }

    await runSave(
      'branding',
      async () => {
        await schoolsApi.updateTheme(schoolId, { theme })
        setBranding((prev) => ({ ...prev, appName: resolvedAppName }))
        syncSessionSchoolConfig({
          appName: resolvedAppName,
          theme,
          logoUrl: theme.logoUrl || '',
        })
      },
      'Theme updated successfully'
    )
  }

  const handleSaveAdmissionFormat = async () => {
    if (!schoolId) return
    if (!admissionFormat.includes('{SEQ')) {
      toast.error('Format must include {SEQ}, {SEQ3}, or {SEQ5}')
      return
    }

    await runSave(
      'admission',
      async () => {
        await schoolsApi.updateAdmissionFormat(schoolId, { admissionFormat })
        syncSessionSchoolConfig({ admissionFormat })
      },
      `Admission format saved. Next preview: ${admissionPreview}`
    )
  }

  const handleSaveRazorpay = async () => {
    if (!schoolId) return
    if (!razorpay.keyId.trim()) {
      toast.error('Razorpay Key ID is required')
      return
    }

    const needsNewSecret = !razorpay.hasKeySecret || isChangingRazorpaySecret
    if (needsNewSecret && !razorpay.keySecret.trim()) {
      toast.error('Razorpay Key Secret is required')
      return
    }

    try {
      setSavingSection('payments')

      const payload = {
        keyId: razorpay.keyId.trim(),
        isEnabled: razorpay.isEnabled,
      }

      if (needsNewSecret) {
        payload.keySecret = razorpay.keySecret.trim()
      }

      const result = await schoolsApi.updateRazorpay(schoolId, payload)

      if (result?.status === 'pending') {
        setPendingApproval({ approvalId: result.approvalId })
        setIsChangingRazorpaySecret(false)
        setRazorpay((prev) => ({ ...prev, keySecret: '' }))
        toast.success('Razorpay settings submitted for approval')
        return
      }

      setPendingApproval(null)
      setIsChangingRazorpaySecret(false)
      setRazorpay((prev) => ({ ...prev, keySecret: '', hasKeySecret: true }))
      toast.success('Razorpay settings saved')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save Razorpay settings')
    } finally {
      setSavingSection('')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="School Settings"
          subtitle="This page now follows the Edubridge RN admin flow: landing editor, school info, app branding, admission format, and Razorpay setup."
          chips={[
            schoolLabel,
            school.schoolCode ? `Code: ${school.schoolCode}` : 'School code pending',
            `Template: ${branding.reportCardTemplate}`,
            razorpay.isEnabled ? 'Online payments enabled' : 'Online payments off',
          ]}
          actions={
            <button onClick={() => fetchSettings({ silent: true })} className="btn-secondary" disabled={refreshing}>
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
          }
        />

        {!schoolId && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            Your school is not linked yet, so school-level settings cannot be loaded.
          </div>
        )}

        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUploadLogo}
        />

        <button
          onClick={() => navigate('/landing')}
          className="soft-panel w-full px-5 py-4 text-left transition-colors hover:border-primary-200"
        >
          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
              <School className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-primary-700">Landing Page Editor</p>
              <p className="mt-1 text-sm text-gray-500">Gallery, principal message, toppers, achievements, and school stats.</p>
            </div>
            <ArrowRight className="h-4 w-4 text-primary-600" />
          </div>
        </button>

        <div className="card p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-100 text-gray-700">
              <Info className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">School Info</p>
              <h3 className="mt-2 text-xl font-bold text-gray-900">School identity used throughout the admin app</h3>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {[
                  { label: 'School Name', value: school.name || 'Not added yet' },
                  { label: 'School Code', value: school.schoolCode || 'Not added yet' },
                  { label: 'Email', value: school.email || 'Not added yet' },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{item.label}</p>
                    <p className="mt-2 text-sm font-semibold text-gray-900 break-words">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6 space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
                <Palette className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">App Branding</p>
                <h3 className="mt-2 text-xl font-bold text-gray-900">Logo, colors, dark mode, and report card template</h3>
                <p className="mt-2 text-sm text-gray-500">This now matches the same editing flow RN admins get in School Settings.</p>
              </div>
            </div>
            <button onClick={() => logoInputRef.current?.click()} className="btn-secondary" disabled={isSaving('logo')}>
              {isSaving('logo') ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload Logo
            </button>
          </div>

          <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
            <div className="space-y-4">
              <div className="rounded-[28px] p-5 text-white shadow-sm" style={{ background: `linear-gradient(135deg, ${branding.primaryColor}, ${branding.secondaryColor})` }}>
                <div className="flex items-center gap-3">
                  <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-white/15 backdrop-blur-sm">
                    {branding.logoUrl ? (
                      <img src={branding.logoUrl} alt={resolveBrandName(branding.appName, school.name)} className="h-full w-full object-cover" />
                    ) : (
                      <School className="h-6 w-6" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-[0.18em] text-white/75">Brand Preview</p>
                    <p className="mt-1 truncate text-lg font-bold">{resolveBrandName(branding.appName, school.name)}</p>
                  </div>
                </div>
                <div className="mt-5 rounded-2xl bg-white/12 px-4 py-3 backdrop-blur-sm">
                  <p className="text-sm font-semibold">{branding.darkMode ? 'Dark mode is enabled' : 'Light-first experience'}</p>
                  <p className="mt-1 text-xs text-white/70">Report card template: {branding.reportCardTemplate}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center">
                {branding.logoUrl ? (
                  <img src={branding.logoUrl} alt="School logo" className="mx-auto h-28 w-28 rounded-2xl object-contain" />
                ) : (
                  <>
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm">
                      <ImagePlus className="h-6 w-6 text-gray-400" />
                    </div>
                    <p className="mt-4 text-sm font-semibold text-gray-700">No logo uploaded</p>
                    <p className="mt-1 text-xs text-gray-500">Use Upload Logo to add your school mark.</p>
                  </>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600">App Name</label>
                <input
                  className="input-field w-full"
                  value={branding.appName}
                  onChange={(event) => setBranding((prev) => ({ ...prev, appName: event.target.value }))}
                  placeholder="e.g. Delhi Public School"
                />
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <label className="block text-xs font-semibold text-gray-600">Primary Color</label>
                    <span className="h-4 w-4 rounded-full border border-white shadow-sm" style={{ backgroundColor: branding.primaryColor }} />
                    <span className="text-xs text-gray-500">{branding.primaryColor}</span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={`primary-${color}`}
                        type="button"
                        onClick={() => setBranding((prev) => ({ ...prev, primaryColor: color }))}
                        className={`h-9 w-9 rounded-full border-4 transition-transform hover:scale-105 ${branding.primaryColor === color ? 'border-gray-900' : 'border-white shadow-sm'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <label className="block text-xs font-semibold text-gray-600">Secondary Color</label>
                    <span className="h-4 w-4 rounded-full border border-white shadow-sm" style={{ backgroundColor: branding.secondaryColor }} />
                    <span className="text-xs text-gray-500">{branding.secondaryColor}</span>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={`secondary-${color}`}
                        type="button"
                        onClick={() => setBranding((prev) => ({ ...prev, secondaryColor: color }))}
                        className={`h-9 w-9 rounded-full border-4 transition-transform hover:scale-105 ${branding.secondaryColor === color ? 'border-gray-900' : 'border-white shadow-sm'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl px-4 py-3 text-sm font-semibold text-white" style={{ backgroundColor: branding.primaryColor }}>
                  Primary
                </div>
                <div className="rounded-2xl px-4 py-3 text-sm font-semibold text-white" style={{ backgroundColor: branding.secondaryColor }}>
                  Secondary
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Dark Mode</p>
                  <p className="mt-1 text-xs text-gray-500">Enable the same school-wide theme toggle RN admins can save.</p>
                </div>
                <button
                  onClick={() => setBranding((prev) => ({ ...prev, darkMode: !prev.darkMode }))}
                  className={`toggle-switch ${branding.darkMode ? 'bg-primary-600' : 'bg-gray-200'}`}
                >
                  <span className={`toggle-switch-thumb ${branding.darkMode ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              <div>
                <div className="mb-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Report Card Template</p>
                  <p className="mt-2 text-sm text-gray-500">Choose the default print layout for student report cards.</p>
                </div>
                <div className="space-y-3">
                  {REPORT_CARD_TEMPLATES.map((template) => {
                    const isActive = branding.reportCardTemplate === template.key

                    return (
                      <div
                        key={template.key}
                        className={`flex flex-col gap-3 rounded-2xl border px-4 py-4 sm:flex-row sm:items-center ${
                          isActive ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setBranding((prev) => ({ ...prev, reportCardTemplate: template.key }))}
                          className="flex-1 text-left"
                        >
                          <p className={`font-semibold ${isActive ? 'text-primary-700' : 'text-gray-900'}`}>{template.label}</p>
                          <p className={`mt-1 text-sm ${isActive ? 'text-primary-700/80' : 'text-gray-500'}`}>{template.desc}</p>
                        </button>

                        <div className="flex items-center gap-2 sm:self-stretch">
                          <button
                            type="button"
                            onClick={() => setPreviewTemplate(template.key)}
                            className={`btn-secondary ${isActive ? 'border-primary-200 text-primary-700' : ''}`}
                          >
                            <Eye className="h-4 w-4" />
                            Preview
                          </button>
                          {isActive && <CheckCircle2 className="h-5 w-5 text-primary-600" />}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="flex justify-end">
                <button onClick={handleSaveBranding} className="btn-primary" disabled={isSaving('branding')}>
                  {isSaving('branding') ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Theme
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6 space-y-6">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
              <Hash className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Admission Number Format</p>
              <h3 className="mt-2 text-xl font-bold text-gray-900">Student admission IDs follow the same RN setup</h3>
              <p className="mt-2 text-sm text-gray-500">Auto-generated when adding new students, with a live preview that mirrors the mobile flow.</p>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold text-gray-600">Quick Presets</label>
            <div className="flex flex-wrap gap-2">
              {FORMAT_PRESETS.map((preset) => (
                <button
                  key={preset.format}
                  type="button"
                  onClick={() => setAdmissionFormat(preset.format)}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                    admissionFormat === preset.format
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200 hover:text-primary-700'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">Custom Format</label>
            <input
              className="input-field w-full"
              value={admissionFormat}
              onChange={(event) => setAdmissionFormat(event.target.value.toUpperCase())}
              placeholder="{SCHOOL}-{YEAR}-{SEQ}"
            />
          </div>

          <div className="rounded-2xl border border-primary-100 bg-primary-50 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Live Preview</p>
            <p className="mt-3 text-2xl font-extrabold text-primary-700">{admissionPreview}</p>
            <p className="mt-2 text-sm text-primary-700/80">School code: {school.schoolCode || 'SCH'}</p>
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold text-gray-600">Available Tokens</label>
            <div className="grid gap-3 md:grid-cols-2">
              {FORMAT_TOKENS.map((item) => (
                <button
                  key={item.token}
                  type="button"
                  onClick={() => setAdmissionFormat((prev) => `${prev}${item.token}`)}
                  className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:border-primary-200 hover:bg-gray-50"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="rounded-xl bg-primary-50 px-3 py-1 text-xs font-bold text-primary-700">{item.token}</span>
                    <span className="text-xs text-gray-400">e.g. {item.example}</span>
                  </div>
                  <p className="mt-3 text-sm font-semibold text-gray-900">{item.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={handleSaveAdmissionFormat} className="btn-primary" disabled={isSaving('admission')}>
              {isSaving('admission') ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Format
            </button>
          </div>
        </div>

        <div className="card p-6 space-y-6">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Online Fee Payment</p>
              <h3 className="mt-2 text-xl font-bold text-gray-900">Razorpay setup for parent fee payments</h3>
              <p className="mt-2 text-sm text-gray-500">This now follows the RN school settings flow, including change-secret handling and approval submission.</p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Enable Online Payments</p>
              <p className="mt-1 text-xs text-gray-500">Allow parents to pay fees directly to your school bank account.</p>
            </div>
            <button
              onClick={() => setRazorpay((prev) => ({ ...prev, isEnabled: !prev.isEnabled }))}
              className={`toggle-switch ${razorpay.isEnabled ? 'bg-primary-600' : 'bg-gray-200'}`}
            >
              <span className={`toggle-switch-thumb ${razorpay.isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">Razorpay Key ID *</label>
            <input
              className="input-field w-full"
              value={razorpay.keyId}
              onChange={(event) => setRazorpay((prev) => ({ ...prev, keyId: event.target.value }))}
              placeholder="rzp_live_xxxxxxxxxxxx"
              autoComplete="off"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">
              Razorpay Key Secret {(!razorpay.hasKeySecret || isChangingRazorpaySecret) ? '*' : ''}
            </label>

            {razorpay.hasKeySecret && !isChangingRazorpaySecret ? (
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="input-field flex flex-1 items-center text-gray-500">••••••••••••••••••••</div>
                <button
                  type="button"
                  onClick={() => {
                    setIsChangingRazorpaySecret(true)
                    setRazorpay((prev) => ({ ...prev, keySecret: '' }))
                  }}
                  className="btn-secondary"
                >
                  Change Secret
                </button>
              </div>
            ) : (
              <input
                type="password"
                className="input-field w-full"
                value={razorpay.keySecret}
                onChange={(event) => setRazorpay((prev) => ({ ...prev, keySecret: event.target.value }))}
                placeholder={isChangingRazorpaySecret ? 'Enter new key secret' : 'Enter key secret (stored securely)'}
                autoComplete="new-password"
              />
            )}
          </div>

          <div className="rounded-2xl border border-primary-100 bg-primary-50 px-4 py-4">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-700" />
              <div>
                <p className="text-sm font-semibold text-primary-700">How to get Razorpay credentials</p>
                <ol className="mt-2 space-y-1 text-sm text-primary-700/90">
                  <li>1. Sign up free at `razorpay.com`.</li>
                  <li>2. Open Dashboard, then Settings, then API Keys.</li>
                  <li>3. Generate live keys and paste the Key ID and Key Secret here.</li>
                </ol>
              </div>
            </div>
          </div>

          {pendingApproval && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
              <p className="font-semibold">Awaiting super admin approval</p>
              <p className="mt-1">
                Your Razorpay credentials have been submitted and will take effect after approval.
                {pendingApproval.approvalId ? ` Approval ID: ${pendingApproval.approvalId}.` : ''}
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={handleSaveRazorpay} className="btn-primary" disabled={isSaving('payments')}>
              {isSaving('payments') ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Payment Settings
            </button>
          </div>
        </div>
      </div>

      {previewTemplate && (
        <div className="fixed inset-0 z-50 bg-slate-950/55 p-4 md:p-8">
          <div className="mx-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Report Card Preview</p>
                <h3 className="mt-2 text-lg font-bold text-gray-900">
                  {previewTemplate.charAt(0).toUpperCase()}
                  {previewTemplate.slice(1)} Template
                </h3>
              </div>
              <button type="button" onClick={() => setPreviewTemplate(null)} className="btn-secondary">
                <X className="h-4 w-4" />
                Close
              </button>
            </div>

            <div className="min-h-0 flex-1 bg-slate-100">
              <iframe title="Report card template preview" srcDoc={previewHtml} className="h-full w-full bg-white" />
            </div>

            <div className="flex justify-end border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  setBranding((prev) => ({ ...prev, reportCardTemplate: previewTemplate }))
                  setPreviewTemplate(null)
                }}
                className="btn-primary"
              >
                <CheckCircle2 className="h-4 w-4" />
                Use This Template
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
